/**
 * WebSocket client for real-time updates
 */

// ==================== TYPES ====================

export type MessageType =
  // Client -> Server
  | 'subscribe'
  | 'unsubscribe'
  | 'ping'
  // Server -> Client
  | 'connected'
  | 'subscribed'
  | 'unsubscribed'
  | 'pong'
  | 'error'
  // Data updates
  | 'measurement'
  | 'environmental_index'
  | 'alert'
  | 'device_status'
  | 'sensor_health';

export type SubscriptionType = 'room' | 'building' | 'floor' | 'sensor' | 'alerts' | 'system';

export interface WebSocketMessage {
  type: MessageType;
  data?: Record<string, unknown>;
  subscription_type?: SubscriptionType;
  entity_id?: number;
  client_id?: string;
  timestamp?: string;
  error?: string;
}

export interface MeasurementData {
  room_id: number;
  sensor_id: number;
  parameter_type: string;
  value: number;
  unit: string;
  timestamp: string;
}

export interface EnvironmentalIndexData {
  room_id: number;
  index_type: 'MGI' | 'OAQ' | 'RCI' | 'HSI' | 'CGI';
  value: number;
  status: string;
  components?: Record<string, number>;
  timestamp: string;
}

export interface AlertData {
  id: number;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  message: string;
  room_id?: number;
  building_id?: number;
  sensor_id?: number;
  acknowledged: boolean;
  resolved: boolean;
  created_at: string;
}

export interface ConnectionConfig {
  token?: string;
  onConnected?: (clientId: string) => void;
  onDisconnected?: () => void;
  onError?: (error: Event | Error) => void;
  onMessage?: (message: WebSocketMessage) => void;
  onMeasurement?: (data: MeasurementData) => void;
  onEnvironmentalIndex?: (data: EnvironmentalIndexData) => void;
  onAlert?: (data: AlertData) => void;
  autoReconnect?: boolean;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

// ==================== WEBSOCKET CLIENT ====================

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private config: ConnectionConfig;
  private clientId: string | null = null;
  private subscriptions: Map<string, { type: SubscriptionType; entityId: number }> = new Map();
  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private isIntentionallyClosed = false;

  constructor(config: ConnectionConfig = {}) {
    this.config = {
      autoReconnect: true,
      reconnectInterval: 3000,
      maxReconnectAttempts: 10,
      ...config,
    };
  }

  /**
   * Get the WebSocket URL based on current location
   */
  private getWebSocketUrl(): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const configuredUrl = import.meta.env.VITE_WS_URL?.trim();
    const basePath = '/api/v1/ws';
    const tokenParam = this.config.token ? `?token=${this.config.token}` : '';
    if (configuredUrl) {
      const baseUrl =
        configuredUrl.startsWith('ws://') || configuredUrl.startsWith('wss://')
          ? configuredUrl.replace(/\/$/, '')
          : `${protocol}//${configuredUrl.replace(/\/$/, '')}`;
      return `${baseUrl}${basePath}${tokenParam}`;
    }
    return `${protocol}//${window.location.host}${basePath}${tokenParam}`;
  }

  /**
   * Connect to the WebSocket server
   */
  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.isIntentionallyClosed = false;
    const url = this.getWebSocketUrl();

    try {
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        console.log('[WebSocket] Connected');
        this.reconnectAttempts = 0;
        this.startPingInterval();
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('[WebSocket] Failed to parse message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
        this.config.onError?.(error);
      };

      this.ws.onclose = () => {
        console.log('[WebSocket] Disconnected');
        this.stopPingInterval();
        this.config.onDisconnected?.();

        if (!this.isIntentionallyClosed && this.config.autoReconnect) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      console.error('[WebSocket] Connection failed:', error);
      this.config.onError?.(error as Error);
    }
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(message: WebSocketMessage): void {
    this.config.onMessage?.(message);

    switch (message.type) {
      case 'connected':
        this.clientId = message.client_id || null;
        this.config.onConnected?.(this.clientId || '');
        // Resubscribe to previous subscriptions
        this.resubscribeAll();
        break;

      case 'subscribed':
        console.log(`[WebSocket] Subscribed to ${message.subscription_type}:${message.entity_id}`);
        break;

      case 'unsubscribed':
        console.log(`[WebSocket] Unsubscribed from ${message.subscription_type}:${message.entity_id}`);
        break;

      case 'pong':
        // Keepalive response received
        break;

      case 'error':
        console.error('[WebSocket] Server error:', message.error);
        break;

      case 'measurement':
        if (message.data) {
          this.config.onMeasurement?.(message.data as unknown as MeasurementData);
        }
        break;

      case 'environmental_index':
        if (message.data) {
          this.config.onEnvironmentalIndex?.(message.data as unknown as EnvironmentalIndexData);
        }
        break;

      case 'alert':
        if (message.data) {
          this.config.onAlert?.(message.data as unknown as AlertData);
        }
        break;

      case 'device_status':
      case 'sensor_health':
        // Handle other message types as needed
        break;
    }
  }

  /**
   * Subscribe to updates for an entity
   */
  subscribe(type: SubscriptionType, entityId: number): void {
    const key = `${type}:${entityId}`;
    this.subscriptions.set(key, { type, entityId });

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.send({
        type: 'subscribe',
        subscription_type: type,
        entity_id: entityId,
      });
    }
  }

  /**
   * Unsubscribe from updates for an entity
   */
  unsubscribe(type: SubscriptionType, entityId: number): void {
    const key = `${type}:${entityId}`;
    this.subscriptions.delete(key);

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.send({
        type: 'unsubscribe',
        subscription_type: type,
        entity_id: entityId,
      });
    }
  }

  /**
   * Resubscribe to all previous subscriptions (after reconnect)
   */
  private resubscribeAll(): void {
    for (const { type, entityId } of this.subscriptions.values()) {
      this.send({
        type: 'subscribe',
        subscription_type: type,
        entity_id: entityId,
      });
    }
  }

  /**
   * Send a message to the server
   */
  private send(message: Partial<WebSocketMessage>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  /**
   * Start ping interval for keepalive
   */
  private startPingInterval(): void {
    this.stopPingInterval();
    this.pingInterval = setInterval(() => {
      this.send({ type: 'ping' });
    }, 30000); // 30 seconds
  }

  /**
   * Stop ping interval
   */
  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Schedule a reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= (this.config.maxReconnectAttempts || 10)) {
      console.log('[WebSocket] Max reconnect attempts reached');
      return;
    }

    const delay = this.config.reconnectInterval || 3000;
    this.reconnectAttempts++;

    console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  /**
   * Disconnect from the server
   */
  disconnect(): void {
    this.isIntentionallyClosed = true;
    this.stopPingInterval();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.clientId = null;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Get client ID
   */
  getClientId(): string | null {
    return this.clientId;
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<ConnectionConfig>): void {
    this.config = { ...this.config, ...config };
  }
}

// ==================== SINGLETON INSTANCE ====================

let wsClientInstance: WebSocketClient | null = null;

/**
 * Get or create the global WebSocket client instance
 */
export function getWebSocketClient(config?: ConnectionConfig): WebSocketClient {
  if (!wsClientInstance) {
    wsClientInstance = new WebSocketClient(config);
  } else if (config) {
    wsClientInstance.updateConfig(config);
  }
  return wsClientInstance;
}

/**
 * Reset the global WebSocket client
 */
export function resetWebSocketClient(): void {
  if (wsClientInstance) {
    wsClientInstance.disconnect();
    wsClientInstance = null;
  }
}
